import React, { useState, useEffect, useRef, useCallback } from "react";
import type { PrintProduct } from "@/types/PrintProduct";
import { Tooltip } from "@mui/material";


interface PrintSelectorProps {
    products: PrintProduct[];
    orientation: "horizontal" | "vertical" | "square";
    onSelect: (product: PrintProduct) => void;
    onGoBack?: () => void;
    isSaving?: boolean;
}

const PrintSelector: React.FC<PrintSelectorProps> = ({
    products,
    orientation,
    onSelect,
    onGoBack,
    isSaving,
}) => {

    const [selectedId, setSelectedId] = useState<string | null>(null);
    const selectedProduct = products.find(p => p.id === selectedId);
    const containerRef = useRef<HTMLDivElement>(null);
    const thumbnailRefs = useRef<Record<string, HTMLDivElement | null>>({});


    useEffect(() => {
        if (!products.length) return;

        setSelectedId(prev => prev ?? products[0].id);

        if (!setSelectedId && onSelect) {
            onSelect(products[0]);
        }
    }, [products, selectedId, onSelect]);


    const handleSelect = useCallback(
        (product: PrintProduct) => {
            setSelectedId(product.id);
            onSelect(product); //  ONLY ONE SOURCE
        },
        [onSelect]
    );


    useEffect(() => {
        if (!products || products.length === 0 || selectedId === null) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            const currentIndex = products.findIndex(t => t.id === selectedId);
            if (currentIndex === -1) return;

            let newIndex = currentIndex;

            if (e.key === "[") {
                newIndex = Math.max(0, currentIndex - 1);
                e.preventDefault();
            } else if (e.key === "]") {
                newIndex = Math.min(products.length - 1, currentIndex + 1);
                e.preventDefault();
            }

            if (newIndex !== currentIndex) {
                const newTemplate = products[newIndex];
                setSelectedId(newTemplate.id);
                onSelect?.(newTemplate);
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [products, selectedId, onSelect]);


    useEffect(() => {
        if (!selectedId) return;

        const thumb = thumbnailRefs.current[selectedId];
        const container = containerRef.current;
        if (!thumb || !container) return;

        const thumbTop = thumb.offsetTop;
        const thumbBottom = thumbTop + thumb.offsetHeight;
        const containerTop = container.scrollTop;
        const containerBottom = containerTop + container.clientHeight;

        if (thumbTop < containerTop) {
            container.scrollTop = thumbTop;
        } else if (thumbBottom > containerBottom) {
            container.scrollTop = thumbBottom - container.clientHeight;
        }
    }, [selectedId]);


    return (
     <div style={{ height: "100vh", width: "168px", display: "flex", flexDirection: "column", fontFamily: "Arial, sans-serif" }}>

            {/* // EDA logo*/}
            <div
                style={{
                    height: 61,
                    borderRadius: "0px",
                    borderRight: "1px solid #b1b1b1",
                    borderBottom: "1px solid #b1b1b1",
                    borderLeft: "1px solid #b1b1b1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "0 10px",
                    fontSize: 16,
                    backgroundColor: "#fff",
                    position: "sticky",
                    top: 0,
                    zIndex: 100,
                }}>
                <img src={`${process.env.PUBLIC_URL}/EDA_logo_small.png`} alt="EDA Logo" />
            </div>

            {/* Client logo */}
            <div
                style={{
                    height: 45, // container height
                    borderRight: "1px solid #b1b1b1",
                    borderBottom: "1px solid #b1b1b1",
                    borderLeft: "1px solid #b1b1b1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "0 10px",
                    fontSize: 16,
                    backgroundColor: "#fff",
                    position: "sticky",
                    top: 50, // stick under login bar
                    zIndex: 10,
                }}
            >
                <img src={`${process.env.PUBLIC_URL}/in_logo.png`}
                    alt="Logo"
                    style={{
                        height: "40px", //  adjust this value to control logo size
                        objectFit: "contain", // ensures it scales correctly
                    }}
                />
            </div>


            {/* // Templates Bar*/}
            <div style={{
                height: 40,
                borderRight: "1px solid #b1b1b1",
                borderBottom: "1px solid #b1b1b1",
                borderLeft: "1px solid #b1b1b1",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "0 10px",
                fontSize: 15,
                backgroundColor: "#fff",
                position: "sticky",
                top: 50, //  stick under login bar
                zIndex: 10,
                fontWeight: "bold",
                color: '#6f6f6f',
            }}>

                <div className="ImagesName-700">
                    {"Overlays"}
                </div>
                <div className="ImagesName-700">
                    {products.length}
                </div>
            </div>

            {/* Thumbnails */}
            <div
                ref={containerRef} // scrollable container
                style={{
                    flex: 1,
                    display: "flex",
                    backgroundColor: "#fafafa",
                    boxSizing: "border-box",
                    height: "100vh",
                    overflowX: "hidden",
                    overflowY: "auto",
                    padding: 10,
                    position: "relative",
                    alignItems: "flex-start",
                    borderRight: "1px solid #b1b1b1",
                    borderLeft: "1px solid #b1b1b1",
                    paddingBottom: 30,
                    zIndex: 10,
                }}
            >
                <div style={{ display: "flex", flexWrap: "wrap", gap: 15 }}>
                    {products.map((p) => {
                        const isSelected = p.id === selectedId;

                        if (
                            orientation === "square" &&
                            (p.id === "white-border" || p.id === "black-border")
                        ) {
                            return null;
                        }

                        const variant = p.variants?.[orientation];

                        if (!variant) {
                            return null;
                        }

         

                        return (
                            <div
                                key={p.id}
                                onClick={() => handleSelect(p)}
                                style={{
                                    userSelect: "none",
                                    border: isSelected
                                        ? "2px solid #59b3d8"
                                        : "4px solid transparent",
                                }}
                            >
                                <div style={{ display: "flex", justifyContent: "center" }}>
                                    <img
                                        src={variant.thumbnail}
                                        alt={p.name}
                                        style={{
                                            maxWidth: "100%",
                                            maxHeight: "100%",
                                            objectFit: "contain",
                                        }}
                                        draggable={false}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* bottom bar */}
            <div style={{
                height: 33,
                borderTop: "1px solid #b1b1b1",
                borderLeft: "1px solid #b1b1b1",
                borderRight: "1px solid #b1b1b1",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "0 10px",
                fontSize: 14,
                backgroundColor: "#fff",
                position: "sticky",  // keep it sticky
                bottom: 0,           // stick to bottom
                zIndex: 10,
                flexShrink: 0,
            }}
            >

                <Tooltip title="Go back to Product Selection (auto save)" arrow>
                    <button
                        onClick={() => {
                            console.log("Back button clicked"); // <--- should appear
                            if (onGoBack) onGoBack();
                        }}
                        disabled={isSaving}
                        style={{
                            padding: 0,
                            borderRadius: 4,
                            backgroundColor: "transparent",
                            border: "none",
                            cursor: isSaving ? "not-allowed" : "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                          <img
                            src={`${process.env.PUBLIC_URL}/back_to_albums.png`}
                            alt="Back to Product Selection"
                            style={{ width: 45, height: 25 }}
                        />
                    </button>

                </Tooltip>

                {/* Display selected template name next to the button */}
                <span style={{ fontWeight: "bold", color: "#6f6f6f", fontSize:13 }}>
                    {selectedProduct?.name || ""}
                </span>
            </div>

        </div>
    );
};


export default PrintSelector;